import axios from "@core/plugins/axios.plugin"
import { ServiceError } from "@core/exceptions"
import { handleServiceCall } from "@services/helpers"

/** Géométrie GeoJSON retournée par le géocodage */
export type GeocodeGeometry = {
    type: "Point"
    coordinates: [number, number]
}

/** Réponse de l’API Bati GET /geocode (alignée sur api-bati get_geocode_models) */
export type GeocodeResponse = {
    geometry: GeocodeGeometry
    city: string
    postcode: string
    citycode: string
    street: string
    housenumber: string
    level: "housenumber" | "street" | "postcode" | "city"
    score: number
}

/** Bounding box WGS84 (EPSG:4326), alignée sur GET /buildings/bbox */
export type BuildingsBboxParams = {
    lat_min: number
    lat_max: number
    lon_min: number
    lon_max: number
}

/** Géométrie MultiPolygon retournée par l’API bati (GET /buildings/bbox) */
export type BuildingBboxGeometry = {
    type: "MultiPolygon"
    coordinates: number[][][][]
}

/** Élément bâtiment dans la bbox (api-bati BuildingItem) */
export type BuildingBboxItem = {
    id_building: string
    geometry: BuildingBboxGeometry
}

/** Corps de réponse GET /buildings/bbox */
export type BuildingsBboxResponse = {
    buildings: BuildingBboxItem[]
}

/**
 * Service d’appel à l’API Bati (bâtiment, géocodage).
 */
export class BatiService {
    /**
     * Géocode une adresse textuelle et retourne les informations structurées (dont code INSEE citycode).
     * @param address - Adresse à géocoder (format libre, ex. "198 Avenue de France 75013 Paris")
     * @returns Réponse avec citycode, postcode, city, street, etc. ; lance en cas d’adresse non trouvée / erreur
     */
    async geocode(address: string): Promise<GeocodeResponse> {
        return handleServiceCall<GeocodeResponse>(async () => {
            const { data } = await axios.get<GeocodeResponse>(
                "/api/bati/geocode",
                { params: { address } }
            )
            return data
        })
    }

    /**
     * Liste les bâtiments dont la géométrie intersecte la bounding box (GET /buildings/bbox).
     * Aligné sur l’ancien `getBuildingsLocalisations` (legacy) et sur {@link BuildingsBboxResponse}.
     *
     * @param bounds - Limites sud-ouest / nord-est (lat_min, lat_max, lon_min, lon_max)
     * @returns Liste des bâtiments (max. 200 côté API)
     */
    async getBuildingsLocalisations(
        bounds: BuildingsBboxParams
    ): Promise<BuildingBboxItem[]> {
        return handleServiceCall<BuildingBboxItem[]>(async () => {
            const { data } = await axios.get<
                BuildingsBboxResponse & { error?: unknown }
            >("/api/bati/buildings/bbox", {
                params: {
                    lat_min: bounds.lat_min,
                    lat_max: bounds.lat_max,
                    lon_min: bounds.lon_min,
                    lon_max: bounds.lon_max,
                },
            })

            if (
                data != null &&
                typeof data === "object" &&
                "error" in data &&
                data.error
            ) {
                throw new ServiceError(
                    {
                        message:
                            "Impossible de récupérer les bâtiments pour cette zone",
                        code: "bati_bbox_client_error",
                    },
                    400
                )
            }

            return data.buildings ?? []
        })
    }
}
